import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import {
  SupportTicketFilterNames,
  supportTicketFilterData,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import AtSignIcon from "@redotech/redo-icons/at-sign_filled.svg";
import { getTeamUsersQueryKey } from "@redotech/redo-merchant-app-common/hooks/use-team-users";
import type { RedoPlatformRpcClient } from "@redotech/redo-merchant-platform-rpc/client";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import type { ArrayStringTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import type { QueryClient } from "@tanstack/react-query";
import { userNameCache } from "./assignees-filter";

const TEAM_USERS_STALE_TIME_MS = 60_000;

export function createMentionsAdvancedFilter(
  queryClient?: QueryClient,
  platformRpc?: RedoPlatformRpcClient,
): ArrayStringTableFilter<string> {
  return {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: AtSignIcon,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    data: supportTicketFilterData[SupportTicketFilterNames.MENTIONS],
    itemLabel: "mention",
    itemLabelPlural: "mentions",
    displayName: "Mentions",
    dbSearch: true,
    // Reuse the same react-query cache as `useTeamUsers`, so opening this
    // filter and rendering the autocomplete share a single network fetch.
    valueFetcher:
      queryClient && platformRpc
        ? async (_params, _signal) => {
            const users = await queryClient.fetchQuery({
              queryKey: getTeamUsersQueryKey(),
              queryFn: async () => (await platformRpc.getTeamUsers(null)).users,
              staleTime: TEAM_USERS_STALE_TIME_MS,
            });
            for (const u of users) {
              const mongoUserId = MongoUserId.fromString(u._id);
              userNameCache.set(mongoUserId, u.name);
            }
            return users.map((u) => MongoUserId.fromString(u._id));
          }
        : undefined,
    display: (item: string) => userNameCache.get(item) ?? item,
  };
}
